import { Component, OnInit, EventEmitter, inject } from '@angular/core';
import { FieldConfig, ITrailingButtonEvent } from '../field.interface';
import { FormGroup } from '@angular/forms';
import { AppService } from '@app/app.service';
import * as moment from 'moment';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { CUSTOM_DATETIME_FORMATS, CustomDateTimePickerAdapter } from '@app/core/date-adapter';
import { getLanguage } from '@app/core/material.module';
import { NGX_MAT_DATE_FORMATS, NgxMatDateAdapter } from '@angular-material-components/datetime-picker';
import { takeUntil } from 'rxjs/operators';
import { TuiDestroyService } from '@taiga-ui/cdk';

@Component({
    selector: 'app-date-time',
    templateUrl: './date-time.component.html',
    styleUrls: ['../input/input.scss', '../date/date.component.scss'],
    providers: [
        TuiDestroyService,
        { provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATETIME_FORMATS },
        { provide: MAT_DATE_LOCALE, deps: [AppService], useFactory: getLanguage },
        { provide: NgxMatDateAdapter, useClass: CustomDateTimePickerAdapter, deps: [MAT_DATE_LOCALE] },
    ],
})
export class DateTimeComponent implements OnInit {
    readonly #destroy$: TuiDestroyService = inject(TuiDestroyService);
    field: FieldConfig;
    group: FormGroup;
    data: any;
    max = null;
    min = null;

    /** Событие TrailingButton */
    outputEvent: EventEmitter<ITrailingButtonEvent> = null;

    dataFilter = (d: Date): boolean => {
        if (!this.max && !this.min) {
            return true;
        }
        if (!this.max && this.min) {
            return this.min <= d;
        }
        if (this.max && !this.min) {
            return this.max >= d;
        }
        if (this.max && this.min) {
            return this.max >= d >= this.min;
        }
    };

    constructor(public appService: AppService) {}

    ngOnInit(): void {
        this.getMax();
        this.getMin();
        if (this.data) {
            if (this.data.maxField && this.group.get(this.data.maxField)) {
                this.group
                    .get(this.data.maxField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMax();
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.maxField).markAsTouched();
                        this.group.get(this.data.maxField).updateValueAndValidity({ emitEvent: false });
                    });
            }
            if (this.data.minField && this.group.get(this.data.minField)) {
                this.group
                    .get(this.data.minField)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.getMin();
                    });

                this.group
                    .get(this.field.name)
                    .valueChanges.pipe(takeUntil(this.#destroy$))
                    .subscribe(() => {
                        this.group.get(this.data.minField).markAsTouched();
                        this.group.get(this.data.minField).updateValueAndValidity({ emitEvent: false });
                    });
            }
        }
        if (this.field.disabled) {
            setTimeout(() => {
                this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
            });
        }
    }

    /**
     * Получение максимального ограничения
     */
    getMax() {
        if (this.data && this.data.maxDate) {
            this.max = this.data.maxDate;
        } else if (this.data && this.data.maxField && this.group.get(this.data.maxField)) {
            if (typeof this.group.get(this.data.maxField).value == 'string') {
                this.max = moment(this.group.get(this.data.maxField).value);
            } else {
                this.max = this.group.get(this.data.maxField).value;
            }
        } else {
            this.max = null;
        }
    }

    /**
     * Получение минимального ограничения
     */
    getMin() {
        if (this.data && this.data.minDate) {
            this.min = this.data.minDate;
        } else if (this.data && this.data.minField && this.group.get(this.data.minField)) {
            if (typeof this.group.get(this.data.minField).value == 'string') {
                this.min = moment(this.group.get(this.data.minField).value);
            } else {
                this.min = this.group.get(this.data.minField).value;
            }
        } else {
            this.min = null;
        }
    }

    /**
     * Изменение даты
     */
    changeEvent($event: ClipboardEvent) {
        let ctrlValue = this.group.get(this.field.name)?.value ? this.group.get(this.field.name).value : null;
        if ($event) {
            ctrlValue = $event?.clipboardData?.getData('text');
        }
        if (typeof ctrlValue === 'string') {
            ctrlValue = moment(ctrlValue, 'DD.MM.YYYY HH:mm');
        }
        this.group.get(this.field.name).setValue(ctrlValue);
    }

    /**
     * Изменить доступность редактирования значения компоненты
     * @param disabled Компонента доступна или недоступна для редактирования?
     */
    setDisabled(disabled: boolean) {
        this.field.disabled = disabled;
        if (this.field.disabled) {
            this.group.get(this.field.name).disable({ onlySelf: true, emitEvent: false });
        } else {
            this.group.get(this.field.name).enable({ onlySelf: true, emitEvent: false });
        }
    }
}
